import { useEffect, useRef, useState } from "react";
import { Stack, Typography, Button } from "@mui/material";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import { Icon } from "@iconify/react";

type Props = {
  name: string;
  label: string;
  width?: number | string;
  height?: number | string;
  placeholder?: string;
};

export function RHFUploadImage({
  name,
  label,
  width = 250,
  height = 150,
  placeholder = "Click to upload photo",
}: Props) {
  const { control, setValue } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileValue = useWatch({ control, name });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (fileValue instanceof File || fileValue instanceof Blob) {
      const url = URL.createObjectURL(fileValue);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (typeof fileValue === "string" && fileValue) {
      setPreviewUrl(fileValue);
    } else if (!fileValue) {
      setPreviewUrl(null);
    }
  }, [fileValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const openFileDialog = () => inputRef.current?.click();

        const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (
          e
        ) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (!file.type.startsWith("image/")) return;

          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          setValue(name, file, { shouldValidate: true });
          field.onChange(file);
        };

        const clearPhoto = () => {
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setValue(name, null, { shouldValidate: true });
          if (inputRef.current) inputRef.current.value = "";
        };

        return (
          <Stack sx={{ flexDirection: "column" }}>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onFileChange}
            />

            <Stack
              onClick={openFileDialog}
              role="button"
              tabIndex={0}
              sx={{
                width,
                height,
                border: (theme) =>
                  `2px dashed ${error ? theme.palette.error.main : theme.palette.divider}`,
                borderRadius: 2,
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                backgroundImage: previewUrl ? `url(${previewUrl})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                bgcolor: (theme) =>
                  previewUrl ? "transparent" : theme.palette.background.paper,
              }}
            >
              {!previewUrl && (
                <Typography variant="body2" color="text.secondary">
                  {placeholder}
                </Typography>
              )}

              {previewUrl && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileDialog();
                    }}
                  >
                    <Icon icon={"material-symbols:change-circle-outline"} />
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearPhoto();
                    }}
                  >
                    <Icon icon={"material-symbols:delete-outline-rounded"} />
                  </Button>
                </Stack>
              )}
            </Stack>
            <Typography variant="subtitle1" textAlign={"center"}>
              {label}
            </Typography>
            {error && (
              <Typography
                variant="caption"
                color="error"
                textAlign={"center"}
                sx={{ mt: 0.5, fontWeight: 500 }}
              >
                {error.message}
              </Typography>
            )}
          </Stack>
        );
      }}
    />
  );
}
